"use client";

import { useState, useEffect, useMemo, useRef } from "react";
import { Music, Users, TrendingUp, Play, ExternalLink, Disc3, Pause, Clock } from "lucide-react";

interface SpotifyContextSidebarProps {
  eventTitle: string;
  marketQuestions: string[];
}

interface SpotifyTrack {
  id: string;
  name: string;
  artists: { id: string; name: string }[];
  album: {
    id: string;
    name: string;
    image: string | null;
    releaseDate: string;
  };
  duration: number;
  popularity: number;
  previewUrl: string | null;
  spotifyUrl: string | null;
  explicit: boolean;
  artistInfo: {
    followers: number;
    genres: string[];
    popularity: number;
    image: string | null;
  } | null;
  // Market data
  marketChance?: number;
}

interface SpotifyArtist {
  id: string;
  name: string;
  image: string | null;
  followers: number;
  popularity: number;
  genres: string[];
  spotifyUrl: string | null;
  topTracks: {
    id: string;
    name: string;
    album: string;
    albumImage: string | null;
    duration: number;
    popularity: number;
    previewUrl: string | null;
    spotifyUrl: string | null;
  }[];
}

// Extract song and artist from format: "Song Name - Artist Name"
// e.g., "End of Beginning - Djo" -> { song: "End of Beginning", artist: "Djo" }
function extractSongAndArtist(question: string): { song: string; artist: string } | null {
  // Pattern: Will "Song - Artist" be the #1 song...
  const quotedPattern = /Will\s+"(.+?)\s+-\s+(.+?)"\s+be\s+the\s+#?1/i;
  const match = question.match(quotedPattern);

  if (match && match[1] && match[2]) {
    return {
      song: match[1].trim(),
      artist: match[2].trim(),
    };
  }

  return null;
}

// Extract artist name from market question like "Will Bad Bunny be the top Spotify artist for 2025?"
function extractArtistName(question: string): string | null {
  const patterns = [
    /Will (.+?) be the top/i,
    /Will (.+?) win/i,
    /(.+?) to be/i,
    /(.+?) for top/i,
  ];

  for (const pattern of patterns) {
    const match = question.match(pattern);
    if (match && match[1]) {
      return match[1].trim();
    }
  }
  return null;
}

// Extract chance percentage from question if available
function extractChance(question: string): number | undefined {
  // This would need to be passed from parent, but for now return undefined
  return undefined;
}

// Format large numbers
function formatNumber(num: number): string {
  if (num >= 1_000_000_000) {
    return `${(num / 1_000_000_000).toFixed(1)}B`;
  }
  if (num >= 1_000_000) {
    return `${(num / 1_000_000).toFixed(1)}M`;
  }
  if (num >= 1_000) {
    return `${(num / 1_000).toFixed(1)}K`;
  }
  return num.toString();
}

// Format duration from ms to mm:ss
function formatDuration(ms: number): string {
  const minutes = Math.floor(ms / 60000);
  const seconds = Math.floor((ms % 60000) / 1000);
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
}

// Detect if this is a song/track market vs artist market
function isSongMarket(title: string, questions: string[]): boolean {
  const titleLower = title.toLowerCase();

  // Check title for song indicators
  if (titleLower.includes("#1 song") || titleLower.includes("top song") ||
      titleLower.includes("most streamed song") || titleLower.includes("number 1 song")) {
    return true;
  }

  // Check if questions have "Song - Artist" format
  const hasSongFormat = questions.some(q => extractSongAndArtist(q) !== null);
  return hasSongFormat;
}

export default function SpotifyContextSidebar({ eventTitle, marketQuestions }: SpotifyContextSidebarProps) {
  const [tracks, setTracks] = useState<SpotifyTrack[]>([]);
  const [artists, setArtists] = useState<SpotifyArtist[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTrack, setSelectedTrack] = useState<SpotifyTrack | null>(null);
  const [selectedArtist, setSelectedArtist] = useState<SpotifyArtist | null>(null);
  const [playingTrack, setPlayingTrack] = useState<string | null>(null);
  const [audio, setAudio] = useState<HTMLAudioElement | null>(null);
  const [marketType, setMarketType] = useState<"song" | "artist" | null>(null);

  // Track if we've already fetched to prevent re-fetches
  const hasFetchedRef = useRef(false);

  // Detect if this is a music/Spotify event
  const isMusicEvent = eventTitle.toLowerCase().includes("spotify") ||
                       eventTitle.toLowerCase().includes("artist") ||
                       eventTitle.toLowerCase().includes("music") ||
                       eventTitle.toLowerCase().includes("grammy") ||
                       eventTitle.toLowerCase().includes("song");

  // Stable key for questions to prevent re-fetches on array reference changes
  const questionsKey = useMemo(() => marketQuestions.join("|"), [marketQuestions]);

  useEffect(() => {
    if (!isMusicEvent) return;

    // Prevent duplicate fetches on re-renders
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;

    const isSongType = isSongMarket(eventTitle, marketQuestions);
    setMarketType(isSongType ? "song" : "artist");

    if (isSongType) {
      // Fetch songs/tracks
      const fetchTracks = async () => {
        setLoading(true);
        const songData = marketQuestions
          .map(extractSongAndArtist)
          .filter((data): data is { song: string; artist: string } => data !== null)
          .slice(0, 10);

        try {
          // Fetch all tracks in parallel for better performance
          const results = await Promise.allSettled(
            songData.map(async ({ song, artist }) => {
              const res = await fetch(
                `/api/spotify/track/search?track=${encodeURIComponent(song)}&artist=${encodeURIComponent(artist)}`
              );
              if (!res.ok) return null;
              const trackData = await res.json();
              if (trackData.error) return null;
              return trackData as SpotifyTrack;
            })
          );

          const fetchedTracks = results
            .filter((r): r is PromiseFulfilledResult<SpotifyTrack | null> => r.status === "fulfilled")
            .map(r => r.value)
            .filter((t): t is SpotifyTrack => t !== null);

          // Sort by popularity
          fetchedTracks.sort((a, b) => b.popularity - a.popularity);
          setTracks(fetchedTracks);

          if (fetchedTracks.length > 0) {
            setSelectedTrack(fetchedTracks[0]);
          }
        } finally {
          setLoading(false);
        }
      };

      fetchTracks();
    } else {
      // Fetch artists
      const fetchArtists = async () => {
        setLoading(true);
        const artistNames = marketQuestions
          .map(extractArtistName)
          .filter((name): name is string => name !== null)
          .slice(0, 10);

        try {
          // Fetch all artists in parallel
          const results = await Promise.allSettled(
            artistNames.map(async (name) => {
              const searchRes = await fetch(`/api/spotify/search?q=${encodeURIComponent(name)}`);
              if (!searchRes.ok) return null;
              const searchData = await searchRes.json();
              if (!searchData.id) return null;

              const artistRes = await fetch(`/api/spotify/artist/${searchData.id}`);
              if (!artistRes.ok) return null;
              return await artistRes.json() as SpotifyArtist;
            })
          );

          const fetchedArtists = results
            .filter((r): r is PromiseFulfilledResult<SpotifyArtist | null> => r.status === "fulfilled")
            .map(r => r.value)
            .filter((a): a is SpotifyArtist => a !== null);

          fetchedArtists.sort((a, b) => b.popularity - a.popularity);
          setArtists(fetchedArtists);

          if (fetchedArtists.length > 0) {
            setSelectedArtist(fetchedArtists[0]);
          }
        } finally {
          setLoading(false);
        }
      };

      fetchArtists();
    }
  }, [isMusicEvent, questionsKey, eventTitle]); // Use questionsKey instead of marketQuestions

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (audio) {
        audio.pause();
        audio.src = "";
      }
    };
  }, [audio]);

  const playPreview = (track: { id: string; previewUrl: string | null }) => {
    if (!track.previewUrl) return;

    if (audio) {
      audio.pause();
    }

    if (playingTrack === track.id) {
      setPlayingTrack(null);
      return;
    }

    const newAudio = new Audio(track.previewUrl);
    newAudio.volume = 0.5;
    newAudio.play();
    newAudio.onended = () => setPlayingTrack(null);
    setAudio(newAudio);
    setPlayingTrack(track.id);
  };

  if (!isMusicEvent) return null;

  if (loading) {
    return (
      <div className="bg-gradient-to-br from-[#1DB954]/10 via-[#18181D] to-[#0f0f12] rounded-[5px] border border-[#1DB954]/30 p-4">
        <div className="flex items-center gap-2 mb-4">
          <Disc3 className="w-5 h-5 text-[#1DB954] animate-spin" />
          <span className="text-white font-satoshi font-bold">Loading Spotify data...</span>
        </div>
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="animate-pulse">
              <div className="h-12 bg-[#18181D]/50 rounded-[5px]" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  // SONG MARKET UI
  if (marketType === "song" && tracks.length > 0) {
    return (
      <div className="space-y-4">
        {/* Track Rankings */}
        <div className="bg-gradient-to-br from-[#1DB954]/10 via-[#18181D] to-[#0f0f12] rounded-[5px] border border-[#1DB954]/30 overflow-hidden">
          <div className="bg-gradient-to-r from-[#1DB954] to-[#1ed760] px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Music className="w-4 h-4 text-black" />
              <span className="text-black text-sm font-inter-tight font-bold">
                Song Rankings
              </span>
            </div>
            <span className="text-black/70 text-xs font-inter-tight">
              by Spotify Popularity
            </span>
          </div>

          <div className="p-3 space-y-2 max-h-[350px] overflow-y-auto scrollbar-hide">
            {tracks.map((track, index) => (
              <button
                key={track.id}
                onClick={() => setSelectedTrack(track)}
                className={`w-full flex items-center gap-3 p-2 rounded-[5px] transition-colors ${
                  selectedTrack?.id === track.id
                    ? "bg-[#1DB954]/20 border border-[#1DB954]/50"
                    : "bg-[#18181D]/30 hover:bg-[#18181D]/50"
                }`}
              >
                <span className={`w-6 text-center font-monument font-bold text-sm ${
                  index === 0 ? "text-[#FFD700]" : index === 1 ? "text-[#C0C0C0]" : index === 2 ? "text-[#CD7F32]" : "text-gray-500"
                }`}>
                  {index + 1}
                </span>
                {track.album.image ? (
                  <img
                    src={track.album.image}
                    alt={track.album.name}
                    className="w-10 h-10 rounded object-cover"
                  />
                ) : (
                  <div className="w-10 h-10 rounded bg-[#1DB954]/20 flex items-center justify-center">
                    <Disc3 className="w-5 h-5 text-[#1DB954]" />
                  </div>
                )}
                <div className="flex-1 text-left min-w-0">
                  <p className="text-white font-satoshi font-bold text-sm truncate">
                    {track.name}
                  </p>
                  <p className="text-gray-400 text-xs truncate">
                    {track.artists.map(a => a.name).join(", ")}
                  </p>
                </div>
                <div className="text-right flex items-center gap-2">
                  <div className="flex items-center gap-1">
                    <TrendingUp className="w-3 h-3 text-[#1DB954]" />
                    <span className="text-[#1DB954] font-inter-tight font-bold text-sm">
                      {track.popularity}
                    </span>
                  </div>
                  {track.previewUrl && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        playPreview(track);
                      }}
                      className={`p-1.5 rounded-[5px] transition-colors ${
                        playingTrack === track.id
                          ? "bg-[#1DB954] text-black"
                          : "bg-[#18181D] text-gray-400 hover:text-[#1DB954]"
                      }`}
                    >
                      {playingTrack === track.id ? (
                        <Pause className="w-3 h-3" />
                      ) : (
                        <Play className="w-3 h-3" />
                      )}
                    </button>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Selected Track Details */}
        {selectedTrack && (
          <div className="bg-[#18181D] rounded-[5px] border border-[#1A1A1A] overflow-hidden">
            {/* Track Header */}
            <div className="relative">
              {selectedTrack.album.image && (
                <div className="relative h-40">
                  <img
                    src={selectedTrack.album.image}
                    alt={selectedTrack.album.name}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0f0f12] via-[#0f0f12]/60 to-transparent" />
                </div>
              )}
              <div className={`${selectedTrack.album.image ? 'absolute bottom-3' : 'pt-4'} left-3 right-3`}>
                <div className="flex items-start gap-2">
                  {selectedTrack.explicit && (
                    <span className="px-1.5 py-0.5 bg-white/20 text-white text-[10px] font-bold rounded">
                      E
                    </span>
                  )}
                  <div className="flex-1">
                    <h3 className="text-white font-monument font-bold text-lg leading-tight">
                      {selectedTrack.name}
                    </h3>
                    <p className="text-gray-300 text-sm">
                      {selectedTrack.artists.map(a => a.name).join(", ")}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Track Stats */}
            <div className="p-3 space-y-3">
              {/* Album & Duration */}
              <div className="flex items-center justify-between text-xs text-gray-400">
                <span className="truncate flex-1">{selectedTrack.album.name}</span>
                <span className="flex items-center gap-1 ml-2">
                  <Clock className="w-3 h-3" />
                  {formatDuration(selectedTrack.duration)}
                </span>
              </div>

              {/* Popularity Meter */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-gray-400 text-xs">Spotify Popularity</span>
                  <span className="text-[#1DB954] font-bold text-sm">{selectedTrack.popularity}/100</span>
                </div>
                <div className="h-2 bg-[#18181D] rounded-[5px] overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-[#1DB954] to-[#1ed760] transition-[width] duration-500"
                    style={{ width: `${selectedTrack.popularity}%` }}
                  />
                </div>
              </div>

              {/* Artist Info */}
              {selectedTrack.artistInfo && (
                <div className="pt-2 border-t border-[#1A1A1A]/50">
                  <div className="flex items-center gap-2">
                    {selectedTrack.artistInfo.image ? (
                      <img
                        src={selectedTrack.artistInfo.image}
                        alt="Artist"
                        className="w-8 h-8 rounded-[5px] object-cover"
                      />
                    ) : (
                      <div className="w-8 h-8 rounded-[5px] bg-[#1DB954]/20 flex items-center justify-center">
                        <Users className="w-4 h-4 text-[#1DB954]" />
                      </div>
                    )}
                    <div className="flex-1">
                      <p className="text-white text-xs font-bold">
                        {selectedTrack.artists[0]?.name}
                      </p>
                      <p className="text-gray-500 text-[10px]">
                        {formatNumber(selectedTrack.artistInfo.followers)} followers
                      </p>
                    </div>
                  </div>
                  {selectedTrack.artistInfo.genres.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {selectedTrack.artistInfo.genres.slice(0, 3).map((genre) => (
                        <span
                          key={genre}
                          className="px-2 py-0.5 bg-[#1DB954]/10 text-[#1DB954] text-[10px] font-inter-tight rounded-[5px]"
                        >
                          {genre}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Spotify Link */}
              {selectedTrack.spotifyUrl && (
                <a
                  href={selectedTrack.spotifyUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 w-full py-2 bg-[#1DB954] hover:bg-[#1ed760] text-black rounded-[5px] font-inter-tight font-bold text-sm transition-colors"
                >
                  <Music className="w-4 h-4" />
                  Open in Spotify
                  <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  // ARTIST MARKET UI (existing)
  if (marketType === "artist" && artists.length > 0) {
    return (
      <div className="space-y-4">
        {/* Artist Rankings */}
        <div className="bg-gradient-to-br from-[#1DB954]/10 via-[#18181D] to-[#0f0f12] rounded-[5px] border border-[#1DB954]/30 overflow-hidden">
          <div className="bg-gradient-to-r from-[#1DB954] to-[#1ed760] px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Music className="w-4 h-4 text-black" />
              <span className="text-black text-sm font-inter-tight font-bold">
                Artist Rankings
              </span>
            </div>
            <span className="text-black/70 text-xs font-inter-tight">
              by Spotify Popularity
            </span>
          </div>

          <div className="p-3 space-y-2 max-h-[300px] overflow-y-auto scrollbar-hide">
            {artists.map((artist, index) => (
              <button
                key={artist.id}
                onClick={() => setSelectedArtist(artist)}
                className={`w-full flex items-center gap-3 p-2 rounded-[5px] transition-colors ${
                  selectedArtist?.id === artist.id
                    ? "bg-[#1DB954]/20 border border-[#1DB954]/50"
                    : "bg-[#18181D]/30 hover:bg-[#18181D]/50"
                }`}
              >
                <span className={`w-6 text-center font-monument font-bold text-sm ${
                  index === 0 ? "text-[#FFD700]" : index === 1 ? "text-[#C0C0C0]" : index === 2 ? "text-[#CD7F32]" : "text-gray-500"
                }`}>
                  {index + 1}
                </span>
                {artist.image ? (
                  <img
                    src={artist.image}
                    alt={artist.name}
                    className="w-10 h-10 rounded-[5px] object-cover"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-[5px] bg-[#1DB954]/20 flex items-center justify-center">
                    <Music className="w-5 h-5 text-[#1DB954]" />
                  </div>
                )}
                <div className="flex-1 text-left">
                  <p className="text-white font-satoshi font-bold text-sm truncate">
                    {artist.name}
                  </p>
                  <p className="text-gray-400 text-xs">
                    {formatNumber(artist.followers)} followers
                  </p>
                </div>
                <div className="text-right">
                  <div className="flex items-center gap-1">
                    <TrendingUp className="w-3 h-3 text-[#1DB954]" />
                    <span className="text-[#1DB954] font-inter-tight font-bold text-sm">
                      {artist.popularity}
                    </span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Selected Artist Details */}
        {selectedArtist && (
          <div className="bg-[#18181D] rounded-[5px] border border-[#1A1A1A] overflow-hidden">
            {/* Artist Header */}
            <div className="relative h-32">
              {selectedArtist.image && (
                <img
                  src={selectedArtist.image}
                  alt={selectedArtist.name}
                  className="w-full h-full object-cover"
                />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-[#0f0f12] via-[#0f0f12]/50 to-transparent" />
              <div className="absolute bottom-3 left-3 right-3">
                <h3 className="text-white font-monument font-bold text-lg truncate">
                  {selectedArtist.name}
                </h3>
                <div className="flex items-center gap-3 text-xs text-gray-400">
                  <span className="flex items-center gap-1">
                    <Users className="w-3 h-3" />
                    {formatNumber(selectedArtist.followers)}
                  </span>
                  <span className="flex items-center gap-1">
                    <TrendingUp className="w-3 h-3 text-[#1DB954]" />
                    {selectedArtist.popularity}/100
                  </span>
                </div>
              </div>
            </div>

            {/* Genres */}
            {selectedArtist.genres.length > 0 && (
              <div className="px-3 py-2 border-b border-[#1A1A1A]/50">
                <div className="flex flex-wrap gap-1">
                  {selectedArtist.genres.slice(0, 3).map((genre) => (
                    <span
                      key={genre}
                      className="px-2 py-0.5 bg-[#1DB954]/10 text-[#1DB954] text-[10px] font-inter-tight rounded-[5px]"
                    >
                      {genre}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Top Tracks */}
            <div className="p-3">
              <h4 className="text-gray-400 text-xs font-inter-tight font-bold uppercase tracking-wider mb-2">
                Top Tracks
              </h4>
              <div className="space-y-2">
                {selectedArtist.topTracks.map((track, index) => (
                  <div
                    key={track.id}
                    className="flex items-center gap-2 p-2 rounded-[5px] bg-[#18181D]/30 hover:bg-[#18181D]/50 transition-colors group"
                  >
                    <span className="w-4 text-center text-gray-500 text-xs font-inter-tight">
                      {index + 1}
                    </span>
                    {track.albumImage ? (
                      <img
                        src={track.albumImage}
                        alt={track.album}
                        className="w-8 h-8 rounded object-cover"
                      />
                    ) : (
                      <div className="w-8 h-8 rounded bg-[#18181D] flex items-center justify-center">
                        <Disc3 className="w-4 h-4 text-gray-500" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-xs font-satoshi font-medium truncate">
                        {track.name}
                      </p>
                      <p className="text-gray-500 text-[10px] truncate">
                        {track.album}
                      </p>
                    </div>
                    <span className="text-gray-500 text-[10px] font-inter-tight">
                      {formatDuration(track.duration)}
                    </span>
                    {track.previewUrl && (
                      <button
                        onClick={() => playPreview(track)}
                        className={`p-1.5 rounded-[5px] transition-colors ${
                          playingTrack === track.id
                            ? "bg-[#1DB954] text-black"
                            : "bg-[#18181D] text-gray-400 hover:text-[#1DB954] hover:bg-[#1DB954]/20"
                        }`}
                      >
                        {playingTrack === track.id ? (
                          <Pause className="w-3 h-3" />
                        ) : (
                          <Play className="w-3 h-3" />
                        )}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Spotify Link */}
            {selectedArtist.spotifyUrl && (
              <div className="px-3 pb-3">
                <a
                  href={selectedArtist.spotifyUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 w-full py-2 bg-[#1DB954] hover:bg-[#1ed760] text-black rounded-[5px] font-inter-tight font-bold text-sm transition-colors"
                >
                  <Music className="w-4 h-4" />
                  Open in Spotify
                  <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  // No data found
  return null;
}
